<template>
  <v-dialog
    v-model="dialog"
    max-width="560"
    eager
    :attach="'#app'"
    :retain-focus="false"
  >
    <template #activator="{ on, attrs }">
      <v-list-item dense :disabled="loading" v-bind="attrs" v-on="on">
        <v-list-item-content>
          <v-list-item-title>Alfasign</v-list-item-title>
        </v-list-item-content>
        <v-list-item-action>
          <v-icon>mdi-cog-outline</v-icon>
        </v-list-item-action>
      </v-list-item>
    </template>

    <!-- Modal -->
    <v-card>
      <v-toolbar flat dense>
        <v-toolbar-title>
          {{ $t('credentials.alfasign.title') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon :disabled="loading" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-progress-linear v-if="loading" indeterminate></v-progress-linear>

      <v-card-text>
        <!-- INIT STEP -->
        <div v-show="step==='init'">
          <v-form ref="initForm" v-model="validInit">
            <v-text-field
              v-model.trim="form.signerId"
              :label="$t('credentials.alfasign.signerId')"
              :rules="[rules.required]"
              :disabled="loading"
              hide-details="auto"
            />
            <v-text-field
              v-model.trim="form.phone"
              :label="$t('credentials.alfasign.phoneNumber')"
              :rules="[rules.required, rules.phoneNumber]"
              :disabled="loading"
              hide-details="auto"
              persistent-hint
            />
          </v-form>
          <v-alert v-if="error" type="error" dense outlined class="mt-3">
            {{ error }}
          </v-alert>
        </div>

        <!-- CONFIRM STEP -->
        <div v-show="step==='confirm'">
          <v-form ref="confirmForm" v-model="validConfirm" lazy-validation>
            <v-text-field
              v-model.trim="form.description"
              :label="$t('credentials.content.description')"
              :rules="[rules.required ]"
              :disabled="loading"

              maxlength="50"
            />
            <v-card-subtitle>
              <v-icon>
                mdi-message-lock
              </v-icon>
              <span>

                {{ $t('credentials.alfasign.otpCode') }}
              </span>
            </v-card-subtitle>
            <v-text-field
              v-model.trim="form.otp"
              :label="$t('credentials.alfasign.otpInput')"
              :rules="[rules.required, rules.otp]"
              :disabled="loading"

              hide-details="auto"
              inputmode="numeric"
              maxlength="8"
            />

            <v-text-field
              v-model.trim="form.pin"
              :label="$t('credentials.alfasign.pinCode')"
              :rules="[rules.required, rules.pin]"
              :type="showPin ? 'text' : 'password'"
              :append-icon="showPin ? 'mdi-eye' : 'mdi-eye-off'"
              :disabled="loading "
              hide-details="auto"
              @click:append="showPin = !showPin"
              @keydown.enter="confirm"
            />
          </v-form>

          <v-alert v-if="error" type="error" dense class="mt-3">
            {{ error }}
          </v-alert>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-btn text :disabled="loading" @click="closeDialog">
          {{ $t('main.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="!(validInit && validConfirm)"
          @click="onPrimary"
        >
          {{ step==='init' ? $t('credentials.alfasign.sendCode') : $t('dialog.confirmation.title') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          v-if="step === 'confirm'"
          color="primary"
          :loading="loading"
          text
          @click="onResend"
        >
          {{ $t('credentials.alfasign.resendCodeButton') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import alfa from '@/services/api/alfaRefs.js'

export default {
  name: 'ImportAlfasign',
  props: {
    basePath: { type: String, default: '' },
    defaultExpiresInSec: { type: Number, default: 60 * 5 },
    resendCooldownSec: { type: Number, default: 30 }
  },
  data() {
    return {
      showPin: false,
      dialog: false,
      step: 'init', // 'init' | 'confirm'
      loading: false,
      error: '',
      validInit: false,
      validConfirm: false,
      sessionId: null,
      expiresInSec: 0,
      remaining: 0,
      timer: null,
      resendUntil: 0,
      form: {
        signerId: '',
        phone: '',
        otp: '',
        description: '',
        pin: ''
      },
      rules: {
        required: v => !!v || this.$t('validation.form.requiredField'),
        max64: v => !v || (v.length <= 255),
        phoneNumber: v =>
          /^(\+?\d{8,15}|06\d{7,13})$/
            .test(v) || this.$t('credentials.alfasign.phoneNumberRequired'),
        otp: v =>
          /^\d{6}$/.test(v) ||
        this.$t('credentials.alfasign.otpFormatError'),
        pin: v =>
          /^\d{4,12}$/.test(v) ||
        this.$t('credentials.alfasign.pinFormatError')

      }
    }
  },
  computed: {
    now() { return Date.now() },
    canResend() { return Date.now() >= this.resendUntil }
  },
  beforeDestroy() {
    this.stopTimer()
    this.closeDialog()
  },
  methods: {
    openDialog() {
      this.resetState()
      this.dialog = true
    },
    closeDialog() {
      this.dialog = false
      this.step = 'init'
      this.stopTimer()
    },
    resetState() {
      this.step = 'init'
      this.loading = false
      this.error = ''
      this.sessionId = null
      this.expiresInSec = 0
      this.remaining = 0
      this.stopTimer()
      this.resendUntil = 0
      this.form = { signerId: '', phone: '', otp: '', description: '', pin: '' }
      if (this.$refs.initForm) this.$refs.initForm.resetValidation()
      if (this.$refs.confirmForm) this.$refs.confirmForm.resetValidation()
    },
    startTimer() {
      this.stopTimer()
      this.remaining = this.expiresInSec || this.defaultExpiresInSec
      this.timer = setInterval(() => {
        if (this.remaining > 0) this.remaining--
        else this.stopTimer()
      }, 1000)
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    async onPrimary() {
      this.error = ''
      if (this.step === 'init') {
        const ok = this.$refs.initForm && this.$refs.initForm.validate()
        if (!ok) return
        await this.submitInit()
      } else {
        const ok = this.$refs.confirmForm && this.$refs.confirmForm.validate()
        if (!ok) return
        await this.submitConfirm()
      }
    },
    async submitResend() {
      await this.submitInit()
    },
    async submitInit() {
      this.loading = true
      try {
        const resp = await alfa.initiateCreateAlfaRef({
          signerId: this.form.signerId,
          signerPhoneNumber: this.form.phone
        })

        this.sessionId = resp.sessionId
        this.expiresInSec = resp.expiresInSec || this.defaultExpiresInSec
        this.step = 'confirm'
        this.startTimer()
        this.resendUntil = Date.now() + (this.resendCooldownSec * 1000)
      } catch (err) {
        this.error = err.message || 'Hiba történt'
      } finally {
        this.loading = false
      }
    },
    async submitConfirm() {
      if (!this.sessionId) {
        this.error = 'Nincs tranzakció'
        return
      }
      this.loading = true
      try {
        const req = {
          description: this.form.description,
          signerId: this.form.signerId,
          signerPhoneNumber: this.form.phone,
          sessionId: this.sessionId,
          clientPin: this.form.pin,
          otpCode: this.form.otp
        }
        const resp = await alfa.completeCreateAlfaRef(req)
        this.$emit('imported', resp)
        this.closeDialog()
      } catch (err) {
        this.error = err.message || 'Hiba történt'
      } finally {
        this.loading = false
      }
    }

  }
}
</script>
